import { memo, useCallback } from "react";
import {
    GRID_ACTIONS_COLUMN_TYPE,
    type GridRowIdGetter,
    type GridRowParams
} from "@mui/x-data-grid";
import { FilterVariant } from "consts/filters";
import { TableGridMode } from "consts/table";
import { Filters, MenuItem } from "consts/shipmentTracking";
import { useTransactionTrackingDeliveryCodesActions } from "features/shipmentTracking/actions";
import {
    selectIsShipmentTrackingResourceLoading,
    selectIsShipmentTrackingResourceUninitialized,
    selectShipmentTrackingResourceSlice
} from "features/shipmentTracking/selectors";
import type { TransactionTrackingDeliveryCode } from "features/shipmentTracking/types";
import Resource from "ui/layouts/Resource";
import Table from "ui/widgets/Table";
import ShipmentTrackingProvider from "./ShipmentTrackingProvider";
import ShipmentTrackingDetail from "./ShipmentTrackingDetail";
import useRowActions from "./Actions";

const ShipmentTrackingResource = () => {
    const { getTransactionTrackingDeliveryCodes, downloadCsv } =
        useTransactionTrackingDeliveryCodesActions();
    const getRowActions = useRowActions();

    const getRowId: GridRowIdGetter<TransactionTrackingDeliveryCode> = useCallback(
        ({ coreId }: TransactionTrackingDeliveryCode) => coreId as string,
        []
    );

    return (
        <Resource
            breadcrumbsProps={{
                heading: 'Shipment Tracking',
                links: [{ name: '' }],
                sx: { mb: 0 }
            }}
        >
            <Table
                mode={TableGridMode.ShipmentTracking}
                selectIsTableUninitialized={
                    selectIsShipmentTrackingResourceUninitialized(
                        MenuItem.TransactionTrackingDeliveryCodes
                    )
                }
                selectIsTableLoading={
                    selectIsShipmentTrackingResourceLoading(
                        MenuItem.TransactionTrackingDeliveryCodes
                    )
                }
                selectTableDataSlice={
                    selectShipmentTrackingResourceSlice(
                        MenuItem.TransactionTrackingDeliveryCodes
                    )
                }
                fetchTableData={getTransactionTrackingDeliveryCodes}
                downloadCsv={downloadCsv}
                filterTypes={new Map<FilterVariant, string[]>([
                    [FilterVariant.Text, [
                        Filters.coreId,
                        Filters.merchantId,
                        Filters.merchantName,
                        Filters.transactionId,
                        Filters.idOfTransaction,
                        Filters.orderId,
                        Filters.deliveryTrackingCode,
                        Filters.deliveryProvider,
                        Filters.trackingDeliveryCodeId,
                        Filters.trackingDeliveryCodeRefId,
                        Filters.trackingDeliveryCodeStatus,
                        Filters.trackingDeliveryCodeStatusReason,
                        Filters.trackingDeliveryCodeTrackingProvider,
                        Filters.destinationCountryIso2,
                        Filters.destinationTrackingDeliveryCodeCountryIso2,
                        Filters.transactionStatus,
                        Filters.transactionFundStatus,
                        Filters.currency,
                        Filters.trackingDeliveryCodeDecisions,
                        Filters.trackingDeliveryCodeNotifications
                    ]],
                    [FilterVariant.DateRange, [
                        Filters.createdAt,
                        Filters.updatedAt,
                        Filters.trackingDeliveryCodeCreatedAt,
                        Filters.trackingDeliveryCodeUpdatedAt
                    ]],
                    [FilterVariant.NumericRange, [
                        Filters.transactionAmount,
                        Filters.transactionAmountEur
                    ]]
                ])}
                getRowId={getRowId}
                columns={[
                    {
                        field: GRID_ACTIONS_COLUMN_TYPE,
                        type: GRID_ACTIONS_COLUMN_TYPE,
                        getActions: (params: GridRowParams) =>
                            getRowActions(params as GridRowParams<TransactionTrackingDeliveryCode>)
                    }
                ]}
                initialState={{
                    pinnedColumns: {
                        right: [GRID_ACTIONS_COLUMN_TYPE]
                    }
                }}
                LayoutProps={{ sx: { m: '24px 0px' } }}
                FiltersProps={{ sx: { m: 0 } }}
            />
        </Resource>
    );
};

const ShipmentTracking = () => (
    <ShipmentTrackingProvider>
        <ShipmentTrackingResource />
        <ShipmentTrackingDetail />
    </ShipmentTrackingProvider>
);

export default memo(ShipmentTracking);
